import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, X } from "@phosphor-icons/react";

const POSTER_BG =
  "https://customer-assets.emergentagent.com/job_festurban-live/artifacts/fyeuxroq_URBAN%20FEST%20OK.jpg";

const STEPS_MS = [0, 900, 2200, 3600, 5200, 6600];
const TOTAL_DURATION = 7800;

export default function TeaserIntro({ onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const timers = STEPS_MS.map((t, i) => setTimeout(() => setStep(i), t));
    const endTimer = setTimeout(() => onClose(), TOTAL_DURATION);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(endTimer);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black overflow-hidden teaser-root"
      data-testid="teaser-intro"
    >
      {/* Skip */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white flex items-center justify-center transition-colors"
        data-testid="teaser-skip"
        aria-label="Passer l'intro"
      >
        <X size={18} weight="bold" />
      </button>

      {/* Background poster (fades in later) */}
      <div
        className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
          step >= 4 ? "opacity-40" : "opacity-0"
        }`}
        style={{ backgroundImage: `url(${POSTER_BG})` }}
      />
      <div className="absolute inset-0 bg-black/55" />

      {/* Radial glow pulse */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${
          step >= 1 ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,107,26,0.35) 0%, rgba(0,0,0,0) 60%)",
          animation: step >= 1 ? "teaserPulse 2.4s ease-out infinite" : "none",
        }}
      />

      {/* Diagonal scan line */}
      <div
        className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${
          step >= 2 && step < 5 ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background:
            "linear-gradient(110deg, transparent 40%, rgba(255,195,46,0.18) 50%, transparent 60%)",
          backgroundSize: "250% 100%",
          animation: "teaserScan 2.2s linear infinite",
        }}
      />

      {/* STAGE 1 — date reveal */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
        {/* Pastille lieu */}
        <div
          className={`pill transition-all duration-700 ${
            step >= 0 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
          }`}
        >
          <MapPin size={16} weight="bold" />
          <span>La Roche-sur-Yon · Jean-Yole</span>
        </div>

        {/* Date */}
        <div
          className={`mt-6 flex items-center gap-3 transition-all duration-700 delay-200 ${
            step >= 0 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <Calendar size={28} weight="bold" className="text-[#FF6B1A]" />
          <p className="font-display text-3xl sm:text-5xl tracking-[0.1em]">
            29 · 30 <span className="text-[#FFC32E]">AOÛT</span>{" "}
            <span className="text-[#FF6B1A]">2026</span>
          </p>
        </div>

        {/* Tagline 1 */}
        <p
          className={`mt-10 text-xs sm:text-sm tracking-[0.4em] uppercase text-white/70 transition-opacity duration-700 ${
            step >= 1 && step < 3 ? "opacity-100" : "opacity-0"
          }`}
          style={{ position: "absolute", marginTop: "10rem" }}
        >
          Deux jours. Deux disciplines.
        </p>

        {/* Kinetic words stage 2 */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
            step === 2 ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="text-center space-y-2">
            <p className="font-display text-5xl sm:text-8xl leading-none tracking-tighter teaser-word-a">
              BASKET <span className="text-[#FF6B1A]">3V3</span>
            </p>
            <p className="font-display text-5xl sm:text-8xl leading-none tracking-tighter teaser-word-b">
              FOOT <span className="text-[#FFC32E]">5V5</span>
            </p>
          </div>
        </div>

        {/* Kinetic words stage 3 */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
            step === 3 ? "opacity-100" : "opacity-0"
          }`}
        >
          <div className="text-center space-y-3">
            <p className="font-display text-3xl sm:text-5xl text-white/80">CONCERT · DANSE · GRAFF</p>
            <p className="font-display text-3xl sm:text-5xl text-white/80">TOMBOLA · RESTAURATION</p>
            <p className="mt-4 text-xs tracking-[0.4em] uppercase text-[#FF6B1A]">La ville entre en jeu</p>
          </div>
        </div>

        {/* STAGE 4 — BIG TITLE */}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-700 ${
            step >= 4 ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
        >
          <h1 className="font-display text-7xl sm:text-9xl lg:text-[12rem] leading-[0.85] tracking-tighter text-center">
            <span className="block text-white teaser-urban">URBAN</span>
            <span
              className="block bg-gradient-to-r from-[#FFC32E] via-[#FF6B1A] to-[#E63946] bg-clip-text text-transparent teaser-fest"
              style={{ filter: "drop-shadow(0 0 50px rgba(255,107,26,0.5))" }}
            >
              FEST 2026
            </span>
          </h1>

          {/* CTA — stage 5 */}
          <div
            className={`mt-10 flex flex-wrap gap-4 justify-center transition-all duration-700 ${
              step >= 5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <Link to="/inscription" onClick={onClose} className="btn-brutal" data-testid="teaser-cta">
              Inscrire mon équipe
            </Link>
            <button onClick={onClose} className="btn-brutal btn-brutal-ghost" data-testid="teaser-enter">
              Entrer sur le site
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="absolute bottom-6 inset-x-0 flex justify-center gap-1 px-4">
          {STEPS_MS.map((_, i) => (
            <span
              key={i}
              className={`h-[3px] w-10 transition-all duration-300 ${
                step >= i ? "bg-[#FF6B1A]" : "bg-white/15"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Noise */}
      <div className="noise-overlay" />
    </div>
  );
}
